/**
 * 4-chip rollup row for an entity (cascade impact, capability-map
 * cell, entity detail page). One pill per chip:
 *   resilience       -- R/A/G/unknown
 *   blast_ibs_count  -- IBSes in the blast graph
 *   process_count    -- processes covered
 *   obligation_count -- obligations covered
 *
 * Null values render as "n/a" in muted style -- happens for vendor
 * and product impacts where the chain doesn't accept that
 * entity_kind (per backend/twin/cascade_service.py).
 *
 * Originally CascadeChipStrip in twin/cascade/ (P2.6.e); moved to
 * twin/components/ and renamed in P2.7.b once the same rollup
 * appeared on capability-map cells + entity detail pages. The
 * 5-chip strip referenced in the v2 plan adds a workflow chip on
 * top of these 4; that chip ships when the framework <-> twin
 * entity linkage lands (backlog #429).
 */
import { useTranslation } from "react-i18next";

import type { CascadeChips, ResilienceState } from "shared/api/types";

const RESILIENCE_PILL_CLASSES: Record<ResilienceState, string> = {
  red: "bg-red-100 text-red-900 border-red-200",
  amber: "bg-amber-100 text-amber-900 border-amber-200",
  green: "bg-emerald-100 text-emerald-900 border-emerald-200",
  unknown: "bg-slate-100 text-slate-700 border-slate-200",
};

const NULL_PILL_CLASSES = "bg-slate-50 text-slate-400 border-slate-200 italic";
const COUNT_PILL_CLASSES = "bg-slate-100 text-slate-800 border-slate-200";

interface ChipPillProps {
  label: string;
  value: string;
  classes: string;
  testId: string;
  /** F2 -- what a zero means, where the digit alone does not say. */
  title?: string | undefined;
  /** G2 -- whether this value came from the seal or from the findings
   *  the card is showing. */
  dataSource?: string | undefined;
}

function ChipPill({ label, value, classes, testId, title, dataSource }: ChipPillProps) {
  return (
    <span
      /* H7 -- and between the chips. A flex `gap` is a screen fact and
         nothing else; the strip ran together as one word. */
      data-testid={testId}
      title={title}
      data-source={dataSource}
      className={`inline-flex items-baseline gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${classes}`}
    >
      {/* H7 -- a space, in the text, inside the pill's own padding.
          `gap-1.5` on the row separates the pills on screen and in
          nothing else, so the strip ran together as one word for
          anything taking innerText. A visually-hidden separator would
          have fixed the screen reader and left the copy glued, which
          is the half the walk measures. */}{" "}
      {/* ROUND 13 H7 -- a separator in the TEXT. `gap-1.5` on the flex
          row and the gap between these two spans separate them on
          screen and in nothing else, so the strip read
          "ResilienceredIBSes3Processes1Obligations12" to anything
          taking innerText: a copy into a board pack, a screen reader,
          and the walk's own instrument. The glue class, caught by
          `tests/glueJoins.ts`. */}
      <span className="uppercase tracking-wide text-[10px] opacity-70">{label}</span>{" "}
      <span className="font-semibold tabular-nums">{value}</span>
    </span>
  );
}

export interface EntityChipStripProps {
  chips: CascadeChips;
  /**
   * Round 10 C4 -- drop a chip that has no answer instead of printing
   * "n/a".
   *
   * On the cascade and the capability map, "n/a" MEANS something: the
   * chain does not accept that entity kind, and saying so is the point.
   * On an entity page a null chip means the page never asked -- and it
   * read as "we looked and there is nothing", three lines under a
   * sentence naming three business services. Two different claims
   * wearing one pill.
   *
   * Off by default, so the surfaces where "n/a" is an answer keep it.
   */
  omitNull?: boolean;
  /**
   * Round 18 B17 -- show the resilience seal without its colour. On
   * estate D the cascade printed "RESILIENCE amber" and "red" on 45
   * cards over entities that reach no admitted IBS: a seal over a
   * register nobody has admitted is not a judgement of the bank. The
   * value stays (it is what the chain says); the colour goes.
   */
  unadmittedReach?: boolean;
  /** Round 18 D25 -- the IBS count is the admitted cut; say so. */
  admittedOnly?: boolean;
}

export function EntityChipStrip({
  chips,
  omitNull = false,
  unadmittedReach = false,
  admittedOnly = false,
}: EntityChipStripProps) {
  const { t } = useTranslation();
  const naLabel = t("modules.twin.cascade.chip.notApplicable");
  const show = (v: unknown) => !omitNull || v !== null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {show(chips.resilience) && (
        /* Round 13 G2 -- the chip read the sealed chain, which a
           scheduler pass writes, so between an incident arriving and
           that pass running a card could list a critical open finding
           and print "RESILIENCE green" three lines below it. The
           backend now floors the chip by the findings the card is
           already showing; the title says so, because a reader who
           opens the chain page next must not meet the contradiction
           there instead. */
        <ChipPill
          testId="cascade-chip-resilience"
          label={t("modules.twin.cascade.chip.resilience")}
          value={chips.resilience ?? naLabel}
          title={
            unadmittedReach
              ? t("modules.twin.cascade.chip.resilienceUnadmitted")
              : chips.resilience_source === "open_findings"
                ? t("modules.twin.cascade.chip.resilienceFloored", {
                    sealed: chips.resilience_sealed ?? naLabel,
                  })
                : undefined
          }
          classes={
            chips.resilience && !unadmittedReach
              ? RESILIENCE_PILL_CLASSES[chips.resilience]
              : NULL_PILL_CLASSES
          }
          dataSource={chips.resilience_source}
        />
      )}
      {show(chips.blast_ibs_count) && (
        <ChipPill
          testId="cascade-chip-blast-ibs"
          label={t(
            admittedOnly
              ? "modules.twin.cascade.chip.blastIbsAdmitted"
              : "modules.twin.cascade.chip.blastIbsCount",
          )}
          value={
            chips.blast_ibs_count !== null ? String(chips.blast_ibs_count) : naLabel
          }
          classes={
            chips.blast_ibs_count !== null ? COUNT_PILL_CLASSES : NULL_PILL_CLASSES
          }
        />
      )}
      {show(chips.process_count) && (
        /* Round 11 F2 -- "PROCESSES 0", fifth round, and the round-10
           fix beside it is why it is still here: that removed "IBSES
           0" because an IBS reaches zero IBSes BY CONSTRUCTION, and
           left this one because a zero here is a real answer.
 
           It is a real answer and it is not a measurement. Zero
           processes means no customer journey has been mapped to this
           entity -- a gap in the estate, which the digit renders as a
           quantity, on a card whose next line reads "Rests on 3
           services". So the chip says which of the two it means; a
           non-zero count is still a number, because then it is one. */
        <ChipPill
          testId="cascade-chip-processes"
          label={t("modules.twin.cascade.chip.processes")}
          value={
            chips.process_count === null
              ? naLabel
              : chips.process_count === 0
                ? t("modules.twin.cascade.chip.noneMapped")
                : String(chips.process_count)
          }
          title={
            chips.process_count === 0
              ? t("modules.twin.cascade.chip.noneMappedWhy")
              : undefined
          }
          classes={
            chips.process_count !== null ? COUNT_PILL_CLASSES : NULL_PILL_CLASSES
          }
        />
      )}
      {show(chips.obligation_count) && (
        <ChipPill
          testId="cascade-chip-obligations"
          label={t("modules.twin.cascade.chip.obligations")}
          value={
            chips.obligation_count !== null ? String(chips.obligation_count) : naLabel
          }
          classes={
            chips.obligation_count !== null ? COUNT_PILL_CLASSES : NULL_PILL_CLASSES
          }
        />
      )}
    </div>
  );
}
